import { ReleaseFilters as ReleaseFiltersType } from '../../types/release'
import { Button, IconButton } from './Button'
import { Badge } from './Badge'
import { SearchBar } from './SearchBar'
import { ViewSortControls } from './ViewSortControls'
import pageStyles from '../../pages/PageStyles.module.css'
import releaseStyles from '../releases/Releases.module.css'

interface ReleaseToolbarProps {
  search?: string
  onSearchChange?: (value: string) => void
  onSearchSubmit?: (value: string) => void
  searchPlaceholder?: string
  showFilters: boolean
  onToggleFilters: () => void
  filters: Pick<ReleaseFiltersType, 'genres' | 'styles'>
  onFiltersChange: (update: Partial<ReleaseFiltersType>) => void
  activeFilterCount?: number
  /** Content rendered at the trailing end (right side), before ViewSortControls */
  trailing?: React.ReactNode
  /** Props forwarded to ViewSortControls */
  viewSortProps?: React.ComponentProps<typeof ViewSortControls>
  /** Hide ViewSortControls entirely (e.g. when page provides its own) */
  hideViewSort?: boolean
}

export function ReleaseToolbar({
  search,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = 'Search releases...',
  showFilters,
  onToggleFilters,
  filters,
  onFiltersChange,
  activeFilterCount = 0,
  trailing,
  viewSortProps,
  hideViewSort,
}: ReleaseToolbarProps) {
  const handleRemoveGenre = (genre: string) => {
    const newGenres = filters.genres?.filter(x => x !== genre)
    onFiltersChange({ genres: newGenres?.length ? newGenres : undefined, page: 1 } as any)
  }

  const handleRemoveStyle = (style: string) => {
    const newStyles = filters.styles?.filter(x => x !== style)
    onFiltersChange({ styles: newStyles?.length ? newStyles : undefined, page: 1 } as any)
  }

  return (
    <div className={releaseStyles.searchFilterRow}>
      {onSearchChange && search !== undefined && (
        <SearchBar
          value={search}
          onChange={onSearchChange}
          onSubmit={onSearchSubmit}
          placeholder={searchPlaceholder}
        />
      )}

      <Button
        variant="secondary"
        active={showFilters}
        icon={showFilters ? 'bi bi-funnel-fill' : 'bi bi-funnel'}
        onClick={onToggleFilters}
        data-tour="filter-toggle"
      >
        Filters
        {activeFilterCount > 0 && <Badge count>{activeFilterCount}</Badge>}
      </Button>

      {filters.genres?.map(g => (
        <span key={`g-${g}`} className={releaseStyles.filterChip}>
          {g}
          <IconButton variant="ghost" size="sm" icon="bi bi-x" aria-label={`Remove ${g} filter`} onClick={() => handleRemoveGenre(g)} />
        </span>
      ))}
      {filters.styles?.map(s => (
        <span key={`s-${s}`} className={releaseStyles.filterChip}>
          {s}
          <IconButton variant="ghost" size="sm" icon="bi bi-x" aria-label={`Remove ${s} filter`} onClick={() => handleRemoveStyle(s)} />
        </span>
      ))}

      <div className={pageStyles.controlsTrailing}>
        {trailing}
        {!hideViewSort && <ViewSortControls {...viewSortProps} />}
      </div>
    </div>
  )
}
